import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDown, ArrowUp, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { projects } from "../data/projects";

const INITIAL_COUNT = 6;

const PlaceholderArt = () => (
    <>
        <div
            className="absolute inset-0 bg-dots opacity-30 transition-transform duration-700 group-hover:scale-105"
            style={{
                maskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 75%)",
                WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 75%)",
            }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center transition-transform duration-700 ease-out group-hover:scale-[1.04]">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-white shadow-[0_10px_30px_rgba(112,71,245,0.15)] backdrop-blur-sm">
                <ImagePlus className="h-6 w-6 text-viol/70" />
            </span>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-mist/80">Project cover</p>
            <p className="hand mt-1 text-xl text-viol/80">artwork coming soon ✦</p>
        </div>
    </>
);

const CoverPlaceholder = ({ p, n }) => (
    <div className={`relative h-64 overflow-hidden rounded-[2rem] ring-1 ring-ink/5 transition-shadow duration-500 group-hover:shadow-[0_24px_60px_rgba(112,71,245,0.22)] sm:h-72 ${p.bg}`}>
        {p.cover ? (
            <img
                src={p.cover}
                alt={`${p.title} — project cover`}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                data-testid={`project-cover-image-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
        ) : (
            <PlaceholderArt />
        )}
        <span className="glass absolute left-5 top-5 rounded-full px-4 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-ink">
            {p.industry ? `${p.format} · ${p.industry}` : p.format}
        </span>
        {!p.cover && (
            <span className="pointer-events-none absolute -bottom-6 right-4 select-none text-[7rem] font-extrabold leading-none text-ink/[0.05] transition-transform duration-700 group-hover:-translate-y-2">
                {n}
            </span>
        )}
        <div className="pointer-events-none absolute -bottom-16 left-1/2 h-32 w-2/3 -translate-x-1/2 rounded-full bg-viol/0 blur-3xl transition-colors duration-500 group-hover:bg-viol/20" />
    </div>
);

const ProjectCard = ({ p, i }) => {
    const n = String(i + 1).padStart(2, "0");
    const soon = () => toast.info("Project page coming soon");
    const cta = (
        <>
            View My Work
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
        </>
    );
    return (
        <article className="group transition-transform duration-500 hover:-translate-y-1.5" data-testid={`project-card-${i + 1}`}>
            {p.page ? (
                <Link to={`/work/${p.slug}`} className="block" data-testid={`project-cover-link-${i + 1}`}>
                    <CoverPlaceholder p={p} n={n} />
                </Link>
            ) : (
                <CoverPlaceholder p={p} n={n} />
            )}
            <div className="mt-6 px-1">
                <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-extrabold text-ink sm:text-2xl">
                        <span className="mr-2 font-mono text-xs font-medium text-viol">{n}</span>
                        {p.title}
                    </h3>
                    {p.page ? (
                        <Link
                            to={`/work/${p.slug}`}
                            className="group/cta flex shrink-0 items-center gap-1 text-sm font-bold text-viol transition-colors hover:text-viol-deep"
                            data-testid={`project-cta-${i + 1}`}
                        >
                            {cta}
                        </Link>
                    ) : (
                        <button
                            onClick={soon}
                            className="group/cta flex shrink-0 items-center gap-1 text-sm font-bold text-viol transition-colors hover:text-viol-deep"
                            data-testid={`project-cta-${i + 1}`}
                        >
                            {cta}
                        </button>
                    )}
                </div>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-mist">{p.desc}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-mist/80">
                    {p.industry ? `${p.format} · ${p.industry}` : p.format}
                </p>
            </div>
        </article>
    );
};

export default function Works() {
    const [expanded, setExpanded] = useState(false);
    const extra = projects.slice(INITIAL_COUNT);

    const toggle = () => {
        if (expanded) {
            setExpanded(false);
            if (window.__lenis) window.__lenis.scrollTo("#work", { offset: -90 });
        } else {
            setExpanded(true);
        }
    };

    return (
        <section id="work" className="relative overflow-hidden py-24 lg:py-36" data-testid="works-section">
            <div className="pointer-events-none absolute -right-48 top-24 h-[28rem] w-[28rem] rounded-full bg-viol/10 blur-3xl" />
            <div className="pointer-events-none absolute -left-48 bottom-24 h-[24rem] w-[24rem] rounded-full bg-lav-200 opacity-70 blur-3xl" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <Eyebrow>My Work</Eyebrow>
                        <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            Selected work
                        </h2>
                    </div>
                    <p className="max-w-md text-base leading-relaxed text-mist">
                        A selection of digital products, experiences and design
                        explorations I've worked on.
                    </p>
                </Reveal>

                <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2 lg:gap-x-14">
                    {projects.slice(0, INITIAL_COUNT).map((p, i) => (
                        <Reveal key={p.slug} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:mt-20" : ""}>
                            <ProjectCard p={p} i={i} />
                        </Reveal>
                    ))}
                    <AnimatePresence>
                        {expanded &&
                            extra.map((p, j) => (
                                <motion.div
                                    key={p.slug}
                                    initial={{ opacity: 0, y: 40 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20, transition: { duration: 0.25, delay: 0 } }}
                                    transition={{ duration: 0.7, delay: (j % 4) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                                    className={(INITIAL_COUNT + j) % 2 === 1 ? "md:mt-20" : ""}
                                >
                                    <ProjectCard p={p} i={INITIAL_COUNT + j} />
                                </motion.div>
                            ))}
                    </AnimatePresence>
                </div>

                <Reveal className="mt-20 flex flex-col items-center gap-3">
                    <p className="hand text-xl text-viol/80" aria-hidden="true">
                        {expanded ? "that's everything, for now ✦" : `${projects.length - INITIAL_COUNT} more projects below ✦`}
                    </p>
                    <button
                        onClick={toggle}
                        className="btn-grad-soft group flex items-center gap-2 rounded-full px-9 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:-translate-y-0.5"
                        data-testid="works-load-more-button"
                    >
                        {expanded ? (
                            <>
                                Show Less
                                <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
                            </>
                        ) : (
                            <>
                                Load More Work
                                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                            </>
                        )}
                    </button>
                </Reveal>
            </div>
        </section>
    );
}
